import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { categories, stages } from "./startupConstants";
import { BannerUpload } from "./BannerUpload";
import { StartupFormFields } from "./StartupFormFields";
import { StartupFormData, StartupRole } from "./types";

interface CreateStartupFormProps {
  initialData?: StartupFormData & { id: string };
}

export const CreateStartupForm = ({ initialData }: CreateStartupFormProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>(initialData?.category || categories[0]);
  const [selectedStage, setSelectedStage] = useState<string>(initialData?.stage || stages[0]);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [roles, setRoles] = useState<StartupRole[]>([]);

  const { register, handleSubmit, formState: { errors } } = useForm<StartupFormData>({
    defaultValues: initialData || {
      team_size: 1,
      category: categories[0],
      stage: stages[0]
    }
  });

  const uploadBanner = async (startupId: string): Promise<string | null> => {
    if (!bannerFile) return null;

    const fileExt = bannerFile.name.split('.').pop();
    const filePath = `${startupId}/${Date.now()}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from('startup-banners')
      .upload(filePath, bannerFile);

    if (uploadError) {
      console.error('Error uploading banner:', uploadError);
      throw uploadError;
    }

    const { data } = supabase.storage
      .from('startup-banners')
      .getPublicUrl(filePath);

    return data.publicUrl;
  };

  const createStartupRoom = async (startupId: string, startupName: string) => {
    if (!user) return null;

    try {
      const { data: room, error } = await supabase
        .from('matrix_rooms')
        .insert([
          {
            room_id: `!${startupId}:matrix.org`,
            name: startupName,
            created_by: user.id
          }
        ])
        .select()
        .single();

      if (error) throw error;
      console.log('Created startup room:', room);
      return room;
    } catch (error) {
      console.error('Error creating startup room:', error);
      throw error;
    }
  };

  const onSubmit = async (data: StartupFormData) => {
    if (!user) {
      toast({
        title: "Error",
        description: "You must be logged in to create a startup",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    try {
      let bannerUrl = null;
      
      if (initialData) {
        // Update existing startup
        if (bannerFile) {
          bannerUrl = await uploadBanner(initialData.id);
        }
        
        const { error } = await supabase
          .from('startups')
          .update({
            ...data,
            category: selectedCategory,
            stage: selectedStage,
            ...(bannerUrl && { banner_url: bannerUrl }),
          })
          .eq('id', initialData.id);

        if (error) throw error;

        // Create or update roles
        if (roles.length > 0) {
          const { error: rolesError } = await supabase
            .from('startup_roles')
            .upsert(
              roles.map(role => ({
                startup_id: initialData.id,
                title: role.title,
                description: role.description || null
              }))
            );

          if (rolesError) throw rolesError;
        }

        toast({
          title: "Success",
          description: "Your startup has been updated!",
        });
      } else {
        // Create new startup
        const { data: newStartup, error } = await supabase
          .from('startups')
          .insert([
            {
              ...data,
              category: selectedCategory,
              stage: selectedStage,
              user_id: user.id
            }
          ])
          .select()
          .single();

        if (error) throw error;

        if (bannerFile && newStartup) {
          bannerUrl = await uploadBanner(newStartup.id);
          
          // Update startup with banner URL
          const { error: updateError } = await supabase
            .from('startups')
            .update({ banner_url: bannerUrl })
            .eq('id', newStartup.id);

          if (updateError) throw updateError;
        }

        // Create startup chat room
        if (newStartup) {
          await createStartupRoom(newStartup.id, data.name);
        }

        // Create roles if any
        if (roles.length > 0 && newStartup) {
          const { error: rolesError } = await supabase
            .from('startup_roles')
            .insert(
              roles.map(role => ({
                startup_id: newStartup.id,
                title: role.title,
                description: role.description || null
              }))
            );

          if (rolesError) throw rolesError;
        }

        toast({
          title: "Success",
          description: "Your startup has been created!",
        });
      }

      navigate("/communities/startups/dashboard");
    } catch (error) {
      console.error('Error saving startup:', error);
      toast({
        title: "Error",
        description: "Failed to save startup. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <BannerUpload onBannerChange={(file) => setBannerFile(file)} />
      
      <StartupFormFields
        register={register}
        errors={errors}
        selectedCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
        selectedStage={selectedStage}
        onStageChange={setSelectedStage}
        roles={roles}
        onRolesChange={setRoles}
      />

      <Button 
        type="submit" 
        className="w-full bg-white text-corporate-black hover:bg-corporate-gray-100"
        disabled={isSubmitting}
      >
        {isSubmitting ? (initialData ? "Updating..." : "Creating...") : (initialData ? "Update Startup" : "Create Startup")}
      </Button>
    </form>
  );
};